import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import FocusTrap from 'focus-trap-react';
import { motion } from 'framer-motion';

import useOutsideClick from './../../hooks/useOutsideClick';
import { useModalContext } from './ModalProvider';

function ModalCloseButton({ onClick }) {
	return (
		<button onClick={onClick} type="button" className="gs-modal-close-btn" aria-label="닫기">
			<span className="sr-only">닫기</span>
		</button>
	);
}

function Modal(props) {
	const {
		ariaLabelledby,
		handleOpenModal = () => {},
		className,
		style,
		duration,
		closeButtonFixed,
		disableOutsideClick = true,
		children,
	} = props;

	const ModalRef = useRef();

	const onKeyDown = (e) => {
		if (e.keyCode === 27) handleOpenModal();
	};

	useOutsideClick(ModalRef, () => {
		if (disableOutsideClick) return;
		if (ModalRef.current.classList.contains('opened')) handleOpenModal();
	});

	let setDuration = duration === undefined ? 0.3 : duration;

	return createPortal(
		<FocusTrap>
			<motion.div
				id={`${ariaLabelledby}-wrap`}
				className={
					className
						? `gs-modal-wrap ${ariaLabelledby}-wrap ${className}`
						: `gs-modal-wrap ${ariaLabelledby}-wrap`
				}
				role="dialog"
				tabIndex="-1"
				aria-modal="true"
				aria-labelledby={ariaLabelledby}
				onKeyDown={onKeyDown}
				data-effect-duration={setDuration === 0 ? setDuration : null}
				data-close-btn-pos={closeButtonFixed}
				initial={{ opacity: setDuration === 0 ? 1 : 0 }}
				animate={{ opacity: 1 }}
				transition={{
					duration: setDuration,
				}}
				onAnimationComplete={() => {
					ModalRef.current.classList.add('opened');
				}}
			>
				{closeButtonFixed && <ModalCloseButton onClick={handleOpenModal} />}
				<motion.div
					ref={ModalRef}
					className="gs-modal-content"
					style={style}
					initial={{ opacity: 0, y: '50%' }}
					animate={{ opacity: 1, y: '0%' }}
					transition={{
						duration: setDuration * 2,
						ease: 'backOut',
					}}
				>
					<div>
						{!closeButtonFixed && <ModalCloseButton onClick={handleOpenModal} />}

						{children}
					</div>
				</motion.div>
			</motion.div>
		</FocusTrap>,
		document.body,
	);
}

function Header({ id, className, children }) {
	return (
		<div className={className ? `gs-modal-header ${className}` : 'gs-modal-header'}>
			<h2 className="gs-modal-title" id={id}>
				{children ? children : null}
			</h2>
		</div>
	);
}

function Body({ className, onClick = () => {}, children }) {
	return (
		<div className={className ? `gs-modal-body ${className}` : 'gs-modal-body'} onClick={onClick}>
			<div>{children ? children : null}</div>
		</div>
	);
}

function Footer({ className, children }) {
	return (
		<div className={className ? `gs-modal-footer ${className}` : 'gs-modal-footer'}>
			{children ? children : null}
		</div>
	);
}

// Custom Hooks
function useToggleModal(status = false, name = '') {
	// const [modal, setModal] = useState({ isOpen: status, type: name });

	// const open = (obj) => {
	// 	setModal(obj);
	// };

	// const close = () => {
	// 	open({ isOpen: false, type: '' });
	// };

	// return {
	// 	modal,
	// 	open,
	// 	close,
	// };

	const ctx = useModalContext();

	return {
		modal: ctx.current,   // 기존 modal.name 접근 유지
		stack: ctx.stack,     // 디버깅/확장용
		open: ctx.open,
		close: ctx.close,     // 항상 top만 닫힘
		closeAll: ctx.closeAll,
	};
}

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export { Modal as default, useToggleModal };
